import React, { useEffect, useRef, useCallback, useState } from "react";
import {
  Box,
  Container,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

/* ================= UI COMPONENTS ================= */
import ExamHeader from "./sessionFile/ExamHeader";
import ExamQuestionBody from "./sessionFile/ExamQuestionBody";
import ExamModals from "./sessionFile/ExamModals";

/* ================= ENGINE ================= */
import useSingleExamSession from "./hooks/useSingleExamSession";

export default function ExamSessionPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const autoSubmitRef = useRef(null);

  const single = useSingleExamSession({
    examId,
    api,
    onAutoSubmit: () => autoSubmitRef.current?.(),
  });

  const {
    exam,
    questions,
    answers,
    flags,
    currentIndex,
    currentQ,
    remainingSec,
    saving,
    navBusy,
 
    submittingUI,

    setExam,
    setQuestions,
    setAnswers,
    setFlags,
    setCurrentIndex,
    setRemainingSec,
    setSubmittedUI,
    setSubmittingUI,

    flushQuestionIfDirty,
    handleSelectOption,
    toggleFlag,
    setIndexSafely,
    stopAllIntervals,
    resumeTimer,

    submittedRef,
    submittingRef,
    currentQIdRef,
  } = single;

  const [loading, setLoading] = useState(true);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  /* ================= LOAD SESSION ================= */
  const loadSession = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.get(`/exam/session/${examId}`);
      const examObj = res.data.exam;

      // 🚫 Hard stop cases
      if (!examObj || ["submitted", "expired"].includes(examObj.status)) {
        navigate("/dashboard", { replace: true });
        return;
      }

      // ✅ Allow BOTH in_progress and paused
      setExam(examObj);
      setQuestions(res.data.questions || []);

      const ans = {};
      const flg = {};
      (res.data.answers || []).forEach((a) => {
        ans[a.question_id] = a.selected_option;
        flg[a.question_id] = !!a.flagged;
      });

      setAnswers(ans);
      setFlags(flg);

      // ✅ Always trust server remaining_seconds
      if (Number.isInteger(examObj.remaining_seconds)) {
        setRemainingSec(examObj.remaining_seconds);
      }

      setCurrentIndex(0);

      // 🔑 AUTO-RESUME IF PAUSED
      if (examObj.status === "paused") {
        await resumeTimer();
      }
    } catch (e) {
      navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [
    examId,
    navigate,
    api,
    resumeTimer,
    setExam,
    setQuestions,
    setAnswers,
    setFlags,
    setRemainingSec,
    setCurrentIndex,
  ]);

  /* ================= SUBMIT ================= */
  const handleSubmit = useCallback(
    async (auto = false) => {
      if (submittedRef.current || submittingRef.current) return;

      submittingRef.current = true;
      setSubmittingUI(true);

      try {
        const qid = currentQIdRef.current;
        if (qid) await flushQuestionIfDirty(qid);

        await api.post(`/exam/${examId}/submit`, {
          remaining_seconds: remainingSec,
        });

        submittedRef.current = true;
        setSubmittedUI(true);
        stopAllIntervals();
        await refreshUser();

        if (auto) {
          navigate(`/dashboard/result/${examId}`, { replace: true });
        } else {
          setConfirmSubmit(false);

          setTimeout(() => {
            setSuccessModal(true);
          }, 180);
        }
      } finally {
        submittingRef.current = false;
        setSubmittingUI(false);
      }
    },
    [
      examId,
      remainingSec,
      navigate,
      refreshUser,
      flushQuestionIfDirty,
      stopAllIntervals,
      submittedRef,
      submittingRef,
      currentQIdRef,
      setSubmittedUI,
      setSubmittingUI,
    ]
  );

  useEffect(() => {
    autoSubmitRef.current = () => handleSubmit(true);
  }, [handleSubmit]);

  useEffect(() => {
    loadSession();

    return () => {
      if (single.submittedRef.current || single.submittingRef.current) {
        stopAllIntervals();
      }
    };
  }, []);

  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;

  if (loading) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!exam) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Exam session not found.</Alert>
      </Container>
    );
  }

  const canSubmit = Object.values(answers || {}).some(
    (v) => v != null && String(v).trim() !== ""
  );

  return (
    <Container
      maxWidth="lg"
      className="exam-lock"
      sx={{
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 1, sm: 2 },
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 80px)",
        }}
      >
        <ExamHeader
          exam={exam}
          minutes={minutes}
          seconds={seconds}
          setMapOpen={setMapOpen}
          saving={saving}
          navBusy={navBusy}
          submittingUI={submittingUI}
        />

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            mt: { xs: 1, sm: 2 },
          }}
        >
          <ExamQuestionBody
            questions={questions}
            currentIndex={currentIndex}
            answers={answers}
            flags={flags}
            currentQ={currentQ}
            setIndexSafely={setIndexSafely}
            handleSelectOption={handleSelectOption}
            toggleFlag={toggleFlag}
            saving={saving}
            setConfirmSubmit={setConfirmSubmit}
            navBusy={navBusy}
            submittingUI={submittingUI}
            canSubmit={canSubmit}
          />
        </Box>

        <ExamModals
          mapOpen={mapOpen}
          setMapOpen={setMapOpen}
          questions={questions}
          setIndexSafely={setIndexSafely}
          confirmSubmit={confirmSubmit}
          setConfirmSubmit={setConfirmSubmit}
          answers={answers}
          handleSubmit={handleSubmit}
          successModal={successModal}
          examId={examId}
          navigate={navigate}
        />
      </Box>
    </Container>
  );
}
